import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { encryptURL } from 'utils/functions';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { LançamentosContext } from 'contexts/LançamentosContext';
import { useModal } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import FechamentoModal from './Modals/FechamentoModal';
import ContabilizarModal from './Modals/ContabilizarModal';
import columnOverrides from './columnOverrides';
import getLançamentosFilterFields from './filterFields';

const Lançamentos = () => {
  const rotina = 'LançamentosContabil';
  const titulo = 'Lançamentos Contábeis';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls, deleteGrid } = useContext(GridContext);
  const {
    getLoading,
    postLoading,
    lançamentos,
    getLançamentos,
    postFechamento,
    postContabiliza,
    postRelancamento,
    postFechamentoDelete,
  } = useContext(LançamentosContext);

  const filterFields = useMemo(
    () => getLançamentosFilterFields(drops),
    [drops]
  );

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getLançamentos({ ...props, rotina, filter });
  };

  const cb = () => loadGrid();

  const toolbarActions = [
    {
      name: 'Adicionar Lançamento',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Lancamentos/Gerar'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: lançamentos?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: lançamentos?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={lançamentos?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Contabilizar Lançamentos',
      icon: 'calculate',
      action: () =>
        openModal(
          <ContabilizarModal
            onSubmit={(data) =>
              postContabiliza({
                data: { ...data, contabilizado: 'SIM', filter: getValues() },
                cb,
              })
            }
            text="contabilizar"
          />
        ),
    },
    {
      name: 'Descontabilizar Lançamentos',
      icon: 'cancel',
      action: () =>
        openModal(
          <ContabilizarModal
            onSubmit={(data) =>
              postContabiliza({
                data: { ...data, contabilizado: 'NAO', filter: getValues() },
                cb,
              })
            }
            text="descontabilizar"
          />
        ),
    },
    {
      name: 'Refazer lançamentos',
      icon: 'sync',
      action: () =>
        openModal(
          <ContabilizarModal
            onSubmit={(data) =>
              postRelancamento({
                data: { ...data, filter: getValues() },
                cb,
              })
            }
            text="refazer"
          />
        ),
    },
    {
      name: 'Lançar Fechamento',
      icon: 'done_all',
      action: () =>
        openModal(
          <FechamentoModal onSubmit={(data) => postFechamento({ data, cb })} />,
          80
        ),
    },
    {
      name: 'Excluir Lançamentos de Fechamento',
      icon: 'delete',
      action: () =>
        openModal(
          <ContabilizarModal
            onSubmit={(data) =>
              postFechamentoDelete({
                data: { ...data, filter: getValues() },
                cb,
              })
            }
            text="excluir"
          />
        ),
    },
  ];

  const rowActions = [
    {
      name: 'Editar',
      icon: 'edit',
      menu: true,
      show: ({ row }) => row?.contabilizado !== 'SIM',
      action: ({ row }) =>
        navigate(`/app/Lancamentos/Gerar/${encryptURL(row)}`),
    },
    {
      name: 'Excluir',
      icon: 'delete',
      menu: true,
      show: ({ row }) => row?.contabilizado !== 'SIM',
      action: ({ row }) =>
        openModal(
          <DeleteModal
            onSubmit={() => deleteGrid({ params: { rotina, id: row?.id }, cb })}
          />
        ),
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      menu: true,
      action: ({ row }) =>
        navigate(`/app/Lancamentos/Detalhes/${encryptURL(row)}`),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={lançamentos?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={lançamentos}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          loading={getLoading || postLoading}
          loadGrid={loadGrid}
          getRowHeight={() => 'auto'}
        />
      </Card>
    </Container>
  );
};

export default Lançamentos;
